import React, { useEffect, useState } from 'react'
import { AppData } from 'app/types'
import { Button, IconButton, Spinner } from 'components'
import { getAnalyticsAIContext, saveAnalyticsAIContext, deleteAnalyticsAIContext } from 'services/adminServices'
import { logger } from 'helpers/logger'

interface OverrideRow {
    id: string
    scope: string
    context: string
}

interface AnalyticsAIContextEditorProps {
    app: AppData
    aggregation: string          // e.g. 'funnel_by_source'
    aggregationLabel: string     // e.g. 'Funnel by Source'
    onClose: () => void
}

export function AnalyticsAIContextEditor(props: AnalyticsAIContextEditorProps) {
    const { app, aggregation, aggregationLabel, onClose } = props

    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [success, setSuccess] = useState(false)

    // Hardcoded defaults returned from the backend — the canonical "reset to" values
    const [defaults, setDefaults] = useState<Record<string, string>>({})
    // Saved DB overrides (row objects)
    const [savedOverrides, setSavedOverrides] = useState<Record<string, OverrideRow>>({})

    // What's currently shown in each textarea
    const [systemContext, setSystemContext] = useState('')
    const [aggregationContext, setAggregationContext] = useState('')

    // What was last committed to DB (or the default if no override) — used to detect dirty state
    const [committedSystem, setCommittedSystem] = useState('')
    const [committedAggregation, setCommittedAggregation] = useState('')

    const appId = app?.meta?.id ?? ''

    useEffect(() => {
        loadData()
    }, [appId, aggregation])

    async function loadData() {
        setLoading(true)
        setError(null)
        try {
            const res = await getAnalyticsAIContext(appId)
            const overrides: Record<string, OverrideRow> = res.data?.overrides ?? {}
            const defs: Record<string, string> = res.data?.defaults ?? {}

            setSavedOverrides(overrides)
            setDefaults(defs)

            // Pre-populate: use saved override if present, otherwise use hardcoded default
            const sysVal = overrides['system']?.context ?? defs['system'] ?? ''
            const aggVal = overrides[aggregation]?.context ?? defs[aggregation] ?? ''

            setSystemContext(sysVal)
            setAggregationContext(aggVal)
            setCommittedSystem(sysVal)
            setCommittedAggregation(aggVal)
        } catch (e) {
            logger.error('Failed to load analytics AI context', e)
            setError('Failed to load current settings.')
        } finally {
            setLoading(false)
        }
    }

    const systemDefault = defaults['system'] ?? ''
    const aggregationDefault = defaults[aggregation] ?? ''

    const systemIsOverridden = Boolean(savedOverrides['system']?.id)
    const aggregationIsOverridden = Boolean(savedOverrides[aggregation]?.id)

    // Dirty = user has changed a field from its last-committed value
    const systemDirty = systemContext !== committedSystem
    const aggregationDirty = aggregationContext !== committedAggregation
    const isDirty = systemDirty || aggregationDirty

    async function handleSave() {
        setSaving(true)
        setError(null)
        setSuccess(false)
        try {
            if (systemDirty) {
                if (systemContext.trim() !== systemDefault.trim()) {
                    // Save override
                    await saveAnalyticsAIContext({ app_id: appId || null, scope: 'system', context: systemContext.trim() })
                } else if (systemIsOverridden) {
                    // Value was reset to default manually — delete the override
                    await deleteAnalyticsAIContext(savedOverrides['system'].id)
                }
            }
            if (aggregationDirty) {
                if (aggregationContext.trim() !== aggregationDefault.trim()) {
                    await saveAnalyticsAIContext({ app_id: appId || null, scope: aggregation, context: aggregationContext.trim() })
                } else if (aggregationIsOverridden) {
                    await deleteAnalyticsAIContext(savedOverrides[aggregation].id)
                }
            }
            await loadData()
            setSuccess(true)
            setTimeout(() => setSuccess(false), 3000)
        } catch (e) {
            logger.error('Failed to save analytics AI context', e)
            setError('Failed to save changes.')
        } finally {
            setSaving(false)
        }
    }

    async function handleRestore(scope: 'system' | string) {
        const row = savedOverrides[scope]
        setSaving(true)
        setError(null)
        try {
            if (row?.id) {
                await deleteAnalyticsAIContext(row.id)
            }
            await loadData()
        } catch (e) {
            logger.error('Failed to restore analytics AI context', e)
            setError('Failed to restore default.')
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className="analytics-ai-context-editor">
            <div className="analytics-ai-context-editor-header">
                <div className="analytics-ai-context-editor-title">
                    <i className="fas fa-cog" />
                    <span>AI Analysis Context</span>
                </div>
                <IconButton noBg icon="fas fa-times" onClick={onClose} />
            </div>

            {loading ? (
                <div className="analytics-ai-context-editor-loading">
                    <Spinner showAfter={0} quotes={false} />
                </div>
            ) : (
                <div className="analytics-ai-context-editor-body">
                    <p className="analytics-ai-context-editor-intro">
                        The text shown is the <strong>built-in default</strong> sent to the AI. Edit either field to override it for this app, or restore to revert back to the default.
                    </p>

                    {error && <p className="analytics-ai-context-editor-message error">{error}</p>}
                    {success && <p className="analytics-ai-context-editor-message success">Changes saved.</p>}

                    {/* ── System context ───────────────────────────────────────── */}
                    <div className="analytics-ai-context-editor-field">
                        <div className="analytics-ai-context-editor-field-header">
                            <label>
                                <i className="fas fa-globe" />
                                System Context
                                <span className="analytics-ai-context-editor-scope-note">(applies to all analytics)</span>
                            </label>
                            <button
                                className="analytics-ai-context-editor-reset-btn"
                                onClick={() => handleRestore('system')}
                                disabled={saving || (!systemIsOverridden && systemContext === systemDefault)}
                                title="Restore built-in default"
                            >
                                <i className="fas fa-undo" />
                                Restore default
                            </button>
                        </div>
                        <textarea
                            className={`analytics-ai-context-editor-textarea${systemDirty ? ' dirty' : ''}${systemIsOverridden ? ' overridden' : ''}`}
                            value={systemContext}
                            onChange={e => setSystemContext(e.target.value)}
                            rows={4}
                            disabled={saving}
                        />
                        {systemIsOverridden && !systemDirty && (
                            <span className="analytics-ai-context-editor-override-badge">
                                <i className="fas fa-pencil-alt" /> Custom override active
                            </span>
                        )}
                    </div>

                    {/* ── Aggregation-specific context ─────────────────────────── */}
                    <div className="analytics-ai-context-editor-field">
                        <div className="analytics-ai-context-editor-field-header">
                            <label>
                                <i className="fas fa-chart-bar" />
                                {aggregationLabel} Context
                                <span className="analytics-ai-context-editor-scope-note">(this aggregation only)</span>
                            </label>
                            <button
                                className="analytics-ai-context-editor-reset-btn"
                                onClick={() => handleRestore(aggregation)}
                                disabled={saving || (!aggregationIsOverridden && aggregationContext === aggregationDefault)}
                                title="Restore built-in default"
                            >
                                <i className="fas fa-undo" />
                                Restore default
                            </button>
                        </div>
                        <textarea
                            className={`analytics-ai-context-editor-textarea${aggregationDirty ? ' dirty' : ''}${aggregationIsOverridden ? ' overridden' : ''}`}
                            value={aggregationContext}
                            onChange={e => setAggregationContext(e.target.value)}
                            rows={5}
                            disabled={saving}
                        />
                        {aggregationIsOverridden && !aggregationDirty && (
                            <span className="analytics-ai-context-editor-override-badge">
                                <i className="fas fa-pencil-alt" /> Custom override active
                            </span>
                        )}
                    </div>

                    <div className="analytics-ai-context-editor-actions">
                        <Button onClick={onClose} disabled={saving}>Cancel</Button>
                        {isDirty && (
                            <Button
                                icon={saving ? undefined : 'fas fa-save'}
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? <Spinner showAfter={0} quotes={false} /> : 'Save'}
                            </Button>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}
